const BASE_IMAGE = 'https://partydecider.com/assets/og/og-home.png';
const PLANNER_IMAGE = 'https://partydecider.com/assets/og/og-planner.png';
const WHO_IS_PAYING_IMAGE = 'https://partydecider.com/assets/og/og-whoispaying.png';
const INVITE_IMAGE = 'https://partydecider.com/assets/og/og-invite.png';
const LANDING_IMAGE = 'https://partydecider.com/images/partydecider-og.jpg';

/**
 * Central reference for every share profile. Each key maps to a page or product
 * experience so the text and metadata can be reused across channels or projects.
 */
export const META_PROFILES = {
  home: {
    id: 'home',
    title: 'PartyDecider – Digital Invites, Event & Trip Planner (VIP Ready)',
    description:
      'A free, customizable digital RSVP and event-planning platform built in Canada. Create invites, plan trips, manage tasks, polls, budgets, and VIP-only events.',
    url: 'https://partydecider.com/',
    image: BASE_IMAGE,
    imageAlt: 'PartyDecider · Plan Parties Together, Effortlessly',
    ogType: 'website',
    siteName: 'PartyDecider',
    twitterCard: 'summary_large_image',
    keywords: [
      'PartyDecider',
      'party decider',
      'group decision',
      'who pays game',
      'bill splitter game',
      'event planner app',
      'group poll',
      'random picker',
      'team outing ideas',
      'office lunch decision',
    ],
    robots: 'index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1',
    schema: {
      website: {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'PartyDecider',
        url: 'https://partydecider.com/',
        potentialAction: {
          '@type': 'SearchAction',
          target: 'https://partydecider.com/search?q={search_term_string}',
          'query-input': 'required name=search_term_string',
        },
      },
      organization: {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'FLW Connect Technologies',
        url: 'https://partydecider.com/',
        brand: 'PartyDecider',
        logo: 'https://partydecider.com/images/PartyDecider%20transparent%20logo.png',
        sameAs: [
          'https://www.linkedin.com/company/flw-connect-technologies',
        ],
      },
    },
    share: {
      sms: 'Plan parties and trips with friends hassle-free! PartyDecider lets you create invites, vote on plans, and track RSVPs in one place: https://partydecider.com',
      whatsapp:
        'Say goodbye to endless group chats! PartyDecider makes planning parties and trips easy with shared timelines, polls, and RSVPs: https://partydecider.com',
      x: 'PartyDecider = stress-free event planning with friends. One link, shared timeline, easy RSVPs. Free and fun! https://partydecider.com #PartyPlanning #EventPlanner',
      instagram:
        'Plan events the easy way! Create invites, vote on plans, and coordinate with friends on a shared timeline. Link in bio → https://partydecider.com',
      snapchat:
        'Stop the group chat chaos! Plan parties and trips together with PartyDecider. One link for everything. https://partydecider.com',
    },
  },
  corporateEventSoftware: {
    id: 'corporateEventSoftware',
    title: 'Corporate Event Planning Software | PartyDecider',
    description:
      'Coordinate corporate events, meetings, and offsites with digital invites, RSVP tracking, polls, tasks, budgets, and VIP access controls in one platform.',
    url: 'https://partydecider.com/corporate-event-software',
    image: LANDING_IMAGE,
    imageAlt: 'PartyDecider corporate event planning software overview.',
    ogType: 'website',
    siteName: 'PartyDecider',
    twitterCard: 'summary_large_image',
    robots: 'index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1',
    schema: {
      landing: {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Corporate Event Planning Software – PartyDecider',
        description:
          'PartyDecider unifies invites, RSVPs, polls, tasks, budgets, and VIP access to plan corporate meetings, conferences, and team offsites in one secure place.',
        url: 'https://partydecider.com/corporate-event-software',
        inLanguage: 'en',
        isPartOf: {
          '@type': 'WebSite',
          name: 'PartyDecider',
          url: 'https://partydecider.com'
        },
        primaryImageOfPage: {
          '@type': 'ImageObject',
          url: LANDING_IMAGE
        }
      }
    }
  },
  invitationWeddingServices: {
    id: 'invitationWeddingServices',
    title: 'Online Invitations & Wedding Planning Services | PartyDecider',
    description:
      'Design beautiful digital invitations and plan your wedding with PartyDecider. Create evites, track RSVPs, manage wedding tasks and budgets, and coordinate every event seamlessly.',
    url: 'https://partydecider.com/invitation-wedding-planning-services',
    image: LANDING_IMAGE,
    imageAlt: 'PartyDecider digital invitation and wedding planning overview.',
    ogType: 'website',
    siteName: 'PartyDecider',
    twitterCard: 'summary_large_image',
    robots: 'index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1',
    schema: {
      landing: {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Online Invitations & Wedding Planning Services – PartyDecider',
        description:
          'PartyDecider offers digital invitation and wedding planning tools. Create paperless invites for weddings, birthdays, and events, manage RSVPs, guest lists, tasks, and budgets all in one platform.',
        url: 'https://partydecider.com/invitation-wedding-planning-services',
        inLanguage: 'en',
        isPartOf: {
          '@type': 'WebSite',
          name: 'PartyDecider',
          url: 'https://partydecider.com'
        },
        primaryImageOfPage: {
          '@type': 'ImageObject',
          url: LANDING_IMAGE
        }
      }
    }
  },
  eventPlanningTools: {
    id: 'eventPlanningTools',
    title: 'Trip & Event Planning Tools – Plan Successful Events | PartyDecider',
    description:
      'Plan events and group trips easily with PartyDecider’s all-in-one planning platform. Manage invites, tasks, polls, budgets, and more — ideal for corporate event planning, meetings, and group travel.',
    url: 'https://partydecider.com/event-planning-tools',
    image: LANDING_IMAGE,
    imageAlt: 'PartyDecider trip and event planning tools overview.',
    ogType: 'website',
    siteName: 'PartyDecider',
    twitterCard: 'summary_large_image',
    robots: 'index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1',
    schema: {
      landing: {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Trip & Event Planning Tools – PartyDecider',
        description:
          'PartyDecider’s all-in-one event planner and trip planning tools help you plan events, meetings, and group trips. Manage invitations, RSVPs, tasks, polls, budgets, and more in one platform.',
        url: 'https://partydecider.com/event-planning-tools',
        inLanguage: 'en',
        isPartOf: {
          '@type': 'WebSite',
          name: 'PartyDecider',
          url: 'https://partydecider.com'
        },
        primaryImageOfPage: {
          '@type': 'ImageObject',
          url: LANDING_IMAGE
        }
      }
    }
  },
  about: {
    id: 'about',
    title: 'About PartyDecider — Built in Canada, For Modern Event Planning',
    description:
      'Learn the story behind PartyDecider, a BC-built platform for digital invites, RSVP flows, trip planning, co-hosting, polls, tasks, timelines, and budget tracking.',
    url: 'https://partydecider.com/about',
    image: BASE_IMAGE,
    imageAlt: 'About PartyDecider, a modern event planning platform built in Canada.',
    ogType: 'website',
    siteName: 'PartyDecider',
    twitterCard: 'summary_large_image',
    robots: 'index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1',
  },
  planner: {
    id: 'planner',
    title: 'PartyDecider Planner — Tasks, Polls, Timeline & Budget Tools',
    description:
      'Plan trips, events and parties with co-hosts. Manage tasks, polls, VIP guest lists, payment tracking, and shared milestone timelines in one place.',
    url: 'https://partydecider.com/planner',
    image: PLANNER_IMAGE,
    imageAlt: 'Planner tools for tasks, polls, timelines and budgets in PartyDecider.',
    ogType: 'website',
    siteName: 'PartyDecider',
    twitterCard: 'summary_large_image',
    robots: 'index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1',
  },
  plannerJourney: {
    id: 'plannerJourney',
    title: 'Journey Planner — Build Your Shared Event Timeline',
    description:
      'Turn your event details into a shared journey. Set dates, build milestones, manage guests, budgets, and RSVP flows in one secure invite timeline.',
    url: 'https://partydecider.com/planner/journey',
    image: PLANNER_IMAGE,
    imageAlt: 'Journey planner view showing shared event timeline in PartyDecider.',
    ogType: 'website',
    siteName: 'PartyDecider',
    twitterCard: 'summary_large_image',
    robots: 'index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1',
  },
  faq: {
    id: 'faq',
    title: 'PartyDecider FAQ — Digital RSVP, Trip Planner & VIP Events',
    description:
      'Answers to common questions about PartyDecider\'s digital RSVP tools, VIP events, trip planner, polls, budget tools, co-host planning, and payment tracking.',
    url: 'https://partydecider.com/faq',
    image: BASE_IMAGE,
    imageAlt: 'PartyDecider FAQ about digital RSVP, trip planning, and VIP events.',
    ogType: 'website',
    siteName: 'PartyDecider',
    twitterCard: 'summary_large_image',
    keywords: [
      'digital RSVP',
      'VIP events',
      'trip planner',
      'polls',
      'budget tools',
      'co-host planning',
      'payment tracker',
      'PartyDecider FAQ'
    ],
    robots: 'index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1',
  },
  invite: {
    id: 'invite',
    title: 'PartyDecider Invite — Digital RSVP & Shared Timeline',
    description:
      'Join this event on PartyDecider with secure digital RSVP, shared timelines, trip planning, polls, and budget tools.',
    url: 'https://partydecider.com',
    image: INVITE_IMAGE,
    imageAlt: 'PartyDecider digital invite interface with shared event timeline.',
    ogType: 'website',
    siteName: 'PartyDecider',
    twitterCard: 'summary_large_image',
    robots: 'index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1',
  },
  whoIsPaying: {
    id: 'whoIsPaying',
    title: 'Who Is Paying? — Fun Group Payment Picker',
    description:
      'A fast, fun mini-game to decide who pays the bill. Perfect for friends, dinner nights, trips, and parties.',
    url: 'https://partydecider.com/whoispaying',
    image: WHO_IS_PAYING_IMAGE,
    imageAlt: 'Who Is Paying? Fair random payer game for groups.',
    ogType: 'website',
    siteName: 'PartyDecider',
    twitterCard: 'summary_large_image',
    keywords: [
      'who is paying',
      'who pays the bill game',
      'who pays for dinner',
      'random payer generator',
      'bill splitter game',
      'office lunch who pays',
      'who pays for drinks',
      'friends bill roulette',
      'decide who pays',
      'high card decides who pays',
    ],
    robots: 'index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1',
    schema: {
      webApplication: {
        '@context': 'https://schema.org',
        '@type': 'WebApplication',
        name: 'Who Is Paying?',
        applicationCategory: 'GameApplication',
        url: 'https://partydecider.com/whoispaying',
        operatingSystem: 'Web',
        description:
          'A fair random payer game that helps groups decide who pays for dinner, drinks, or the next round using dice or high-card draws.',
        creator: {
          '@type': 'Organization',
          name: 'FLW Connect Technologies',
        },
      },
      faq: {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'How does Who Is Paying work?',
            acceptedAnswer: {
              '@type': 'Answer',
              text:
                'Create a room, invite your group, choose dice or high card mode, and play rounds. The game randomly selects who pays or wins the agreed award.',
            },
          },
          {
            '@type': 'Question',
            name: 'Is Who Is Paying fair?',
            acceptedAnswer: {
              '@type': 'Answer',
              text:
                'Yes. Each round uses random dice rolls or shuffled card values so every player has an equal chance to win unless you adjust house rules outside the app.',
            },
          },
          {
            '@type': 'Question',
            name: 'Do my friends need an account to play?',
            acceptedAnswer: {
              '@type': 'Answer',
              text:
                'No. You can open a room and share the code with friends or coworkers so they can join the game in their browser without downloading an app.',
            },
          },
        ],
      },
    },
    share: {
      sms: 'Who Is Paying settles the tab with dice or high card. Open a room, invite friends, crown tonight\'s payer: https://partydecider.com/whoispaying',
      whatsapp:
        'Let Who Is Paying decide the bill for office lunches, team outings, or family dinners. Spin up a room now: https://partydecider.com/whoispaying',
      x: 'Skip the awkward math; Who Is Paying uses dice or cards to pick tonight\'s payer. Start a room: https://partydecider.com/whoispaying #SplitTheBill',
      instagram:
        'Who\'s covering dinner? Let PartyDecider\'s Who Is Paying game decide with a tap. Dice or cards, same fair finish. https://partydecider.com/whoispaying',
      snapchat:
        'Swipe to let fate pick up the tab. Who Is Paying makes the call in seconds. https://partydecider.com/whoispaying',
    },
  },
  moments: {
    id: 'moments',
    title: 'PartyDecider Planner · Build Your Shared Timeline',
    description:
      'Turn your plans into a shared timeline of Moments. Add polls, notes, locations, and tasks, then invite your crew to collaborate.',
    url: 'https://partydecider.com/planner',
    image: PLANNER_IMAGE,
    imageAlt: 'Plan in Moments · PartyDecider Planner',
    ogType: 'website',
    siteName: 'PartyDecider',
    twitterCard: 'summary_large_image',
    share: {
      sms: 'Moments by PartyDecider makes event planning easy: shared timelines, polls, tasks, and RSVPs all in one link: https://partydecider.com/planner',
      whatsapp:
        'Plan parties and trips together with Moments! Vote on plans, assign tasks, and track RSVPs on a shared timeline: https://partydecider.com/planner',
      x: 'From birthday parties to group trips, Moments makes collaborative event planning easy. Shared timelines, polls, tasks & RSVPs in one place: https://partydecider.com/planner #EventPlanning',
      instagram:
        'Plan every detail together! Moments gives you shared timelines, polls, tasks, and RSVP tracking for stress-free event planning: https://partydecider.com/planner',
      snapchat:
        'Coordinate parties and trips on one timeline! Moments = polls, tasks, RSVPs, all in one link. https://partydecider.com/planner',
    },
  },
  invite: {
    id: 'invite',
    title: 'You\'re Invited! View the Plan on PartyDecider',
    description:
      'See what\'s happening, RSVP, and join the plan. View all Moments like dinner times, locations, polls, and shared tasks.',
    url: 'https://partydecider.com/i/',
    image: INVITE_IMAGE,
    imageAlt: 'You\'re Invited to a Plan on PartyDecider',
    ogType: 'website',
    siteName: 'PartyDecider',
    twitterCard: 'summary_large_image',
    share: {
      sms: 'You\'re invited! View the plan and RSVP on PartyDecider',
      whatsapp:
        'You\'re invited to the plan! Check out all the Moments, vote, and RSVP',
      x: 'Join the plan! View the timeline, vote on activities, and RSVP #PartyDecider',
      instagram:
        'You\'re invited! See the full plan, vote on activities, and RSVP easily',
      snapchat:
        'Join the plan! View Moments, vote, and RSVP',
    },
  },
  'my-moments': {
    id: 'my-moments',
    title: 'My Plans · Your Event Dashboard & Guest Invites',
    description:
      'View all your hosted events and guest invites in one place. Resume planning, check RSVPs, and see what is next for your parties and trips.',
    url: 'https://partydecider.com/planner/profile',
    image: PLANNER_IMAGE,
    imageAlt: 'My Plans dashboard showing hosted events and guest invites',
    ogType: 'website',
    siteName: 'PartyDecider',
    twitterCard: 'summary_large_image',
    share: {
      sms: 'My Plans keeps all your events organized: hosted parties and guest invites in one dashboard: https://partydecider.com/planner/profile',
      whatsapp:
        'Stay on top of all your events with My Plans. View hosted parties, guest invites, and upcoming plans: https://partydecider.com/planner/profile',
      x: 'My Plans = your personal event dashboard for all hosted and guest events. Never miss a party or trip detail: https://partydecider.com/planner/profile #EventPlanner',
      instagram:
        'Keep track of every event you are planning or attending. My Plans organizes it all in one place: https://partydecider.com/planner/profile',
      snapchat:
        'All your events in one dashboard. My Plans shows hosted parties and guest invites. https://partydecider.com/planner/profile',
    },
  },
  about: {
    id: 'about',
    title: 'About · FLW Connect Technologies · Our Journey',
    description:
      'We started FLW Connect Technologies in Canada to make digital tools simple, local, and affordable. PartyDecider turns group decisions into fun, shared moments.',
    url: 'https://partydecider.com/about',
    image: BASE_IMAGE,
    imageAlt: 'FLW Connect Technologies and PartyDecider brand story.',
    ogType: 'website',
    siteName: 'PartyDecider',
    twitterCard: 'summary_large_image',
    share: {
      sms: 'Learn about FLW Connect Technologies and why we built PartyDecider to make group decisions fun: https://partydecider.com/about',
      whatsapp:
        'Our story: FLW Connect Technologies and the journey to PartyDecider: simple, local, and affordable digital tools. https://partydecider.com/about',
      x: 'From a small team to PartyDecider, FLW Connect Technologies is making decisions fun and tools accessible. https://partydecider.com/about #PartyDecider',
      instagram:
        'About FLW Connect Technologies and PartyDecider, bringing people together through playful decisions and simple tools. https://partydecider.com/about',
      snapchat:
        'Meet FLW Connect Technologies and our journey to PartyDecider. About us: https://partydecider.com/about',
    },
  },
};

export const SHARE_CHANNELS = /** @type {const} */ ([
  'sms',
  'whatsapp',
  'x',
  'instagram',
  'snapchat',
]);

/**
 * Convenience accessor; returns a frozen copy so callers can read without
 * mutating the source.
 * @param {keyof typeof META_PROFILES} key
 */
export function getMetaProfile(key) {
  const profile = META_PROFILES[key];
  if (!profile) throw new Error(`Unknown meta profile: ${key}`);
  return JSON.parse(JSON.stringify(profile));
}
